<template>
  <div>
    <!-- Top filter / actions -->
    <div class="align-items-xl-end d-flex flex-column flex-xl-row justify-content-between mb-3 gap-2">
      <div class="zone-wrapper">
        <ul class="nav nav-pills zone-pills flex-nowrap">
          <li class="cursor-pointer nav-item" @click="setDateFilter('today')">
            <a class="nav-link" :class="{ 'active': filter.range === 'today' }">
              <i class="fa-solid fa-calendar-day me-1"/>{{ $t('Today') }}
              <span class="ms-1 badge bg-light text-dark">{{ counts.today }}</span>
            </a>
          </li>
          <li class="cursor-pointer nav-item" @click="setDateFilter('upcoming')">
            <a class="nav-link" :class="{ 'active': filter.range === 'upcoming' }">
              <i class="fa-solid fa-calendar-plus me-1"/>{{ $t('Upcoming') }}
              <span class="ms-1 badge bg-light text-dark">{{ counts.upcoming }}</span>
            </a>
          </li>
          <li class="cursor-pointer nav-item" @click="setDateFilter('past')">
            <a class="nav-link" :class="{ 'active': filter.range === 'past' }">
              <i class="fa-solid fa-clock-rotate-left me-1"/>{{ $t('Past') }}
              <span class="ms-1 badge bg-light text-dark">{{ counts.past }}</span>
            </a>
          </li>
          <li class="cursor-pointer nav-item" @click="setDateFilter('all')">
            <a class="nav-link" :class="{ 'active': filter.range === 'all' }">
              {{ $t('All') }}
              <span class="ms-1 badge bg-light text-dark">{{ counts.all }}</span>
            </a>
          </li>
        </ul>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <div class="input-group input-group-sm search-input-wrapper">
          <input
            type="search"
            class="form-control shadow-none"
            :placeholder="$t('Search name / phone') + '...'"
            v-model.trim="filter.keyword"
          >
          <span class="input-group-text bg-white">
            <i class="fas fa-search"/>
          </span>
        </div>
        <button class="btn btn-theme fw-bold rounded-3" @click="openCreate">
          <i class="fa-solid fa-plus me-2"/>{{ $t('New Reservation') }}
        </button>
      </div>
    </div>

    <!-- Summary cards -->
    <div class="row g-2 mb-3">
      <div class="col-6 col-md-3">
        <div class="card summary-card border-0 rounded-3 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">
              <i class="fa-solid fa-calendar-day me-1 text-theme"/>{{ $t("Today's Reservations") }}
            </div>
            <div class="fs-4 fw-bold">{{ summary.today }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card summary-card border-0 rounded-3 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">
              <i class="fa-solid fa-hourglass-half me-1 text-warning"/>{{ $t('Pending Today') }}
            </div>
            <div class="fs-4 fw-bold text-warning">{{ summary.pending_today }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card summary-card border-0 rounded-3 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">
              <i class="fa-solid fa-circle-exclamation me-1 text-danger"/>{{ $t('No Table Assigned') }}
            </div>
            <div class="fs-4 fw-bold text-danger">{{ summary.no_table }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card summary-card border-0 rounded-3 shadow-sm h-100">
          <div class="card-body p-3">
            <div class="text-muted small">
              <i class="fa-solid fa-users me-1 text-success"/>{{ $t('Total Guests') }}
            </div>
            <div class="fs-4 fw-bold text-success">{{ summary.total_guests }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Secondary state filter -->
    <div class="d-flex flex-wrap gap-2 mb-3">
      <button
        type="button"
        class="btn btn-sm state-chip"
        :class="filter.state === '' ? 'btn-dark' : 'btn-outline-secondary'"
        @click="setStateFilter('')"
      >
        {{ $t('All States') }}
      </button>
      <button
        type="button"
        v-for="s in states"
        :key="s.value"
        class="btn btn-sm state-chip"
        :class="filter.state === s.value ? ('btn-' + s.color) : ('btn-outline-' + s.color)"
        @click="setStateFilter(s.value)"
      >
        <i :class="s.icon" class="me-1"/>{{ $t(s.label) }}
      </button>
      <button
        type="button"
        class="btn btn-sm state-chip"
        :class="filter.onlyNoTable ? 'btn-danger' : 'btn-outline-danger'"
        @click="filter.onlyNoTable = !filter.onlyNoTable"
      >
        <i class="fa-solid fa-circle-exclamation me-1"/>{{ $t('No Table Only') }}
      </button>
    </div>

    <!-- Card grid -->
    <div class="reservation-grid" v-if="filteredReservations.length > 0">
      <div
        v-for="r in filteredReservations"
        :key="r.id"
        class="reservation-card"
        :class="['state-' + r.state, { 'needs-table': r.state === 'pending' && !r.table_id }]"
        @click="openDetail(r)"
      >
        <div class="rsv-status-bar" :class="'bg-' + stateColor(r.state)"/>
        <div class="rsv-card-body">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div class="rsv-time">
                <i class="fa-regular fa-clock me-1"/>{{ formatTime(r.reserve_at) }}
              </div>
              <div class="rsv-date text-muted small">{{ formatDate(r.reserve_at) }}</div>
            </div>
            <span class="badge rsv-state-badge" :class="'bg-' + stateColor(r.state)">
              <i :class="stateIcon(r.state)" class="me-1"/>{{ $t(stateLabel(r.state)) }}
            </span>
          </div>

          <div class="rsv-customer mb-2">
            <div class="fw-bold text-truncate">
              <i class="fa-solid fa-user me-1 text-muted"/>{{ r.name }}
            </div>
            <div class="text-muted small">
              <i class="fa-solid fa-phone me-1"/>{{ r.telephone }}
            </div>
          </div>

          <div class="rsv-meta d-flex flex-wrap gap-2 mb-2">
            <span v-if="r.table_id" class="rsv-chip">
              <i class="fa-solid fa-chair me-1"/>{{ tableName(r.table_id) }}
              <small class="text-muted ms-1">/ {{ $t(zoneName(r.table_id)) }}</small>
            </span>
            <span v-else class="rsv-chip rsv-chip--alert">
              <i class="fa-solid fa-circle-exclamation me-1"/>{{ $t('No Table Assigned') }}
            </span>
            <span class="rsv-chip">
              <i class="fa-solid fa-user-group me-1"/>{{ formatNumber(r.pax) }} {{ $t('Guests') }}
            </span>
          </div>

          <div v-if="r.note" class="rsv-note text-muted small">
            <i class="fa-solid fa-note-sticky me-1"/>{{ r.note }}
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center text-muted py-5">
      <i class="fa-regular fa-calendar-xmark fa-3x mb-3 opacity-50"/>
      <p class="fw-bold mb-3">{{ $t('No Available Data') }}</p>
      <button class="btn btn-theme text-white fw-bold" @click="openCreate">
        <i class="fa-solid fa-plus me-2"/>{{ $t('New Reservation') }}
      </button>
    </div>

    <!-- Detail modal -->
    <modal-reservation-detail
      v-if="detailModal.open"
      :reservation="detailModal.reservation"
      :tables="tables"
      @close="detailModal.open = false"
      @edit="handleEditFromDetail"
      @open-table="handleOpenTable"
      @cancel-reservation="handleCancelReservation"
      @assign-table="handleAssignTable"
      @delete="handleDelete"
    />

    <!-- Form modal -->
    <modal-reservation-form
      v-if="formModal.open"
      :type="formModal.type"
      :reservation="formModal.reservation"
      :tables="tables"
      :state-options="states"
      @close="formModal.open = false"
      @saved="handleSave"
    />
  </div>
</template>

<script>
import moment from 'moment';
import numeral from 'numeral';
import ModalReservationDetail from './ModalReservationDetail.vue';
import ModalReservationForm from './ModalReservationForm.vue';
import {
  MOCK_RESERVATIONS,
  MOCK_TABLES,
  RESERVATION_STATES
} from './mockData.js';

export default {
  name: 'POSReservations',
  components: { ModalReservationDetail, ModalReservationForm },
  data() {
    return {
      reservations: [...MOCK_RESERVATIONS],
      tables: MOCK_TABLES,
      states: RESERVATION_STATES,
      filter: {
        range: 'today',
        state: '',
        onlyNoTable: false,
        keyword: ''
      },
      detailModal: { open: false, reservation: null },
      formModal:   { open: false, type: 'CREATE', reservation: null },
      nextId: 100
    };
  },
  computed: {
    rangeFilteredReservations() {
      const startOfDay = moment().startOf('day');
      const endOfDay   = moment().endOf('day');
      return this.reservations.filter(r => {
        const at = moment(r.reserve_at);
        if (this.filter.range === 'today')    return at.isBetween(startOfDay, endOfDay, null, '[]');
        if (this.filter.range === 'upcoming') return at.isAfter(endOfDay);
        if (this.filter.range === 'past')     return at.isBefore(startOfDay);
        return true;
      });
    },
    filteredReservations() {
      const kw = (this.filter.keyword || '').toLowerCase();
      return this.rangeFilteredReservations
        .filter(r => !this.filter.state || r.state === this.filter.state)
        .filter(r => !this.filter.onlyNoTable || !r.table_id)
        .filter(r => {
          if (!kw) return true;
          const hay = [r.name, r.telephone, this.tableName(r.table_id)]
            .filter(Boolean).join(' ').toLowerCase();
          return hay.includes(kw);
        })
        .sort((a, b) => {
          // Pending-no-table on the same day first, then by time.
          const aNeedsAttn = a.state === 'pending' && !a.table_id ? 0 : 1;
          const bNeedsAttn = b.state === 'pending' && !b.table_id ? 0 : 1;
          if (aNeedsAttn !== bNeedsAttn) return aNeedsAttn - bNeedsAttn;
          return new Date(a.reserve_at) - new Date(b.reserve_at);
        });
    },
    counts() {
      const startOfDay = moment().startOf('day');
      const endOfDay   = moment().endOf('day');
      return {
        all:      this.reservations.length,
        today:    this.reservations.filter(r => moment(r.reserve_at).isBetween(startOfDay, endOfDay, null, '[]')).length,
        upcoming: this.reservations.filter(r => moment(r.reserve_at).isAfter(endOfDay)).length,
        past:     this.reservations.filter(r => moment(r.reserve_at).isBefore(startOfDay)).length
      };
    },
    summary() {
      const todays = this.reservations.filter(r => moment(r.reserve_at).isSame(moment(), 'day'));
      return {
        today:         todays.length,
        pending_today: todays.filter(r => r.state === 'pending').length,
        no_table:      this.reservations.filter(r => r.state === 'pending' && !r.table_id).length,
        total_guests:  todays
          .filter(r => r.state !== 'cancelled')
          .reduce((s, r) => s + (r.pax || 0), 0)
      };
    }
  },
  methods: {
    setDateFilter(v) { this.filter.range = v; },
    setStateFilter(v) { this.filter.state = v; },
    stateColor(v) {
      const s = this.states.find(x => x.value === v);
      return s ? s.color : 'secondary';
    },
    stateIcon(v) {
      const s = this.states.find(x => x.value === v);
      return s ? s.icon : 'fa-solid fa-circle';
    },
    stateLabel(v) {
      const s = this.states.find(x => x.value === v);
      return s ? s.label : v;
    },
    tableName(id) {
      const t = this.tables.find(x => x.id === id);
      return t ? t.name : '';
    },
    zoneName(id) {
      const t = this.tables.find(x => x.id === id);
      return t ? t.zone : '';
    },
    formatDate(iso) { return iso ? moment(iso).format('ddd, DD MMM YYYY') : '-'; },
    formatTime(iso) { return iso ? moment(iso).format('HH:mm') : '-'; },
    formatNumber(v) { return numeral(v).format('0,0'); },
    openDetail(reservation) {
      this.detailModal.reservation = reservation;
      this.detailModal.open = true;
    },
    openCreate() {
      this.formModal.type = 'CREATE';
      this.formModal.reservation = null;
      this.formModal.open = true;
    },
    handleEditFromDetail(reservation) {
      this.detailModal.open = false;
      this.formModal.type = 'EDIT';
      this.formModal.reservation = { ...reservation };
      this.formModal.open = true;
    },
    handleSave(payload) {
      const enriched = {
        ...payload,
        reserve_at: moment(payload.reserve_at).toISOString()
      };
      if (this.formModal.type === 'CREATE') {
        const newItem = {
          id: this.nextId,
          created_at: new Date().toISOString(),
          customer_id: null,
          ...enriched
        };
        this.nextId += 1;
        this.reservations.unshift(newItem);
        this.$toast.success('<b>' + this.$t('Success') + '</b> — ' + this.$t('Reservation created'));
      } else {
        const idx = this.reservations.findIndex(x => x.id === payload.id);
        if (idx !== -1) {
          this.reservations.splice(idx, 1, { ...this.reservations[idx], ...enriched });
        }
        this.$toast.success('<b>' + this.$t('Success') + '</b> — ' + this.$t('Reservation updated'));
      }
      this.formModal.open = false;
    },
    handleAssignTable({ id, table_id }) {
      const idx = this.reservations.findIndex(x => x.id === id);
      if (idx === -1) return;
      this.reservations[idx].table_id = table_id;
      if (this.detailModal.reservation && this.detailModal.reservation.id === id) {
        this.detailModal.reservation = { ...this.reservations[idx] };
      }
      this.$toast.success('<b>' + this.$t('Table assigned') + '</b> — ' + this.tableName(table_id));
    },
    handleOpenTable(reservation) {
      const self = this;
      this.$swal({
        icon: 'question',
        iconColor: '#FF7043',
        title: this.$t('Open Table') + ' ' + this.tableName(reservation.table_id) + '?',
        html: this.$t('A new dine-in session will be created for') + ' <b>' + reservation.name + '</b>',
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fa-solid fa-check-circle me-1"></i>' + this.$t('Open Table'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-theme text-white',
          cancelButton: 'btn btn-secondary me-2'
        },
        reverseButtons: true,
        preConfirm() {
          const idx = self.reservations.findIndex(x => x.id === reservation.id);
          if (idx !== -1) self.reservations[idx].state = 'arrived';
          self.detailModal.open = false;
          self.$toast.success('<b>' + self.$t('Table Opened') + '</b> — ' + reservation.name);
        }
      });
    },
    handleCancelReservation(reservation) {
      const self = this;
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('Cancel Reservation') + '?',
        html: '<b>' + reservation.name + '</b><br>' + this.$t('You will not be able to undo this action.'),
        showCancelButton: true,
        cancelButtonText: this.$t('Back'),
        confirmButtonText: '<i class="fa-solid fa-ban me-1"></i>' + this.$t('Cancel Reservation'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary me-2'
        },
        reverseButtons: true,
        preConfirm() {
          const idx = self.reservations.findIndex(x => x.id === reservation.id);
          if (idx !== -1) self.reservations[idx].state = 'cancelled';
          self.detailModal.open = false;
          self.$toast.success('<b>' + self.$t('Reservation Cancelled') + '</b> — ' + reservation.name);
        }
      });
    },
    handleDelete(id) {
      const self = this;
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('Delete this Reservation') + '?',
        html: this.$t('You will not be able to undo this action.'),
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t('Confirm'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger me-2',
          cancelButton: 'btn btn-secondary'
        },
        reverseButtons: true,
        preConfirm() {
          self.reservations = self.reservations.filter(r => r.id !== id);
          self.detailModal.open = false;
          self.$toast.success('<b>' + self.$t('Success') + '</b> — ' + self.$t('Delete Success!'));
        }
      });
    }
  }
};
</script>

<style scoped>
.search-input-wrapper {
  min-width: 260px;
}
.search-input-wrapper .form-control,
.search-input-wrapper .input-group-text {
  box-shadow: none;
}

.summary-card { background: #fff; }

.state-chip {
  font-weight: 600;
  border-radius: 50rem;
  padding: 4px 14px;
}

.reservation-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding-bottom: 60px;
}

@media (min-width: 576px) {
  .reservation-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 992px) {
  .reservation-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (min-width: 1400px) {
  .reservation-grid { grid-template-columns: repeat(4, 1fr); }
}

.reservation-card {
  position: relative;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  overflow: hidden;
}
.reservation-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
}
.reservation-card:active { transform: scale(0.99); }

.reservation-card.needs-table {
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.25), 0 2px 6px rgba(0, 0, 0, 0.05);
}

.rsv-status-bar {
  height: 4px;
  width: 100%;
}

.rsv-card-body { padding: 14px 16px; }

.rsv-time {
  font-size: 1.25rem;
  font-weight: 800;
  color: #343a40;
  line-height: 1.1;
}
.rsv-date { margin-top: 2px; }

.rsv-state-badge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 5px 9px;
  border-radius: 999px;
}

.rsv-customer .fw-bold {
  font-size: 0.95rem;
  color: #212529;
}

.rsv-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.05);
  color: #495057;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
}

.rsv-chip--alert {
  background: rgba(220, 53, 69, 0.12);
  color: #b02a37;
}

.rsv-note {
  background: #fff8e6;
  border-left: 3px solid #ffc107;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: normal;
  word-break: break-word;
}

.state-cancelled { opacity: 0.7; }
.state-arrived .rsv-time { color: #198754; }
</style>
